import { Component, OnInit } from '@angular/core';
import { IDeployPriorityAtomicRule } from '../../interfaces/deploy-priority/deploy-priority-atomic-rule';
import { IDeployPriorityCompoundRule } from '../../interfaces/deploy-priority/deploy-priority-compound-rule';
import { DeployPriorityAtomicRulesService } from '../../services';
import { DeployPriorityAtomicRuleProperty } from '../../enums/deploy-priority-atomic-rule-property.enum';
import { DeployPriorityAtomicRuleOperator } from '../../enums/deploy-priority-atomic-rule-operator.enum';
import { computed, observable } from 'mobx-angular';
import { DeployPriorityCompoundRuleOperator } from '../../enums/deploy-priority-compound-rule-operator.enum';
import { DeployPriorityGradingsService } from '../../services';
import { IDeployPriorityGrading } from '../../interfaces/deploy-priority/deploy-priority-grading';
import { MessageService, SelectItem } from 'primeng';
import { Message } from '../../classes/Message';
import { DeployPriorityCompoundRulesService } from '../../services';
import { flatMap } from 'lodash';

@Component({
  selector: 'deploy-priority-grading-rules',
  templateUrl: './deploy-priority-grading-rules.component.html',
  styleUrls: ['./job-deploy-priority-gradings.component.scss'],
})
export class DeployPriorityGradingRulesComponent implements OnInit {
  @observable public propertyToTitleMapping: Map<DeployPriorityAtomicRuleProperty, string> =
    new Map<DeployPriorityAtomicRuleProperty, string>([
      [DeployPriorityAtomicRuleProperty.AutoDeploymentScore, 'Auto Deploy Probability'],
      [DeployPriorityAtomicRuleProperty.NonDeploymentScore, 'Non Deploy Probability'],
      [DeployPriorityAtomicRuleProperty.ManuallyFlagged, 'Manually Flagged'],
      [DeployPriorityAtomicRuleProperty.SensitivityScore, 'Sensitivity Score'],
      [DeployPriorityAtomicRuleProperty.HoursUntilDue, 'Hours until due'],
      [DeployPriorityAtomicRuleProperty.Rescheduled, 'Rescheduled'],
    ]);

  public propertyOptions: SelectItem[] = [];

  @observable public operatorMapping: Map<DeployPriorityAtomicRuleOperator, string> = new Map<
    DeployPriorityAtomicRuleOperator,
    string
  >([
    [DeployPriorityAtomicRuleOperator.GreaterThan, '>'],
    [DeployPriorityAtomicRuleOperator.GreaterThanOrEqualTo, '>='],
    [DeployPriorityAtomicRuleOperator.LessThan, '<'],
    [DeployPriorityAtomicRuleOperator.LessThenOrEqualTo, '<='],
    [DeployPriorityAtomicRuleOperator.Equals, '='],
  ]);

  public operatorOptions: SelectItem[] = [];

  public gradingOptions: SelectItem[] = [];

  @observable public atomicRules: IDeployPriorityAtomicRule[] = [];

  @observable public compoundRules: IDeployPriorityCompoundRule[] = [];

  public updateAtomicRuleDto: IDeployPriorityAtomicRule;

  public updateCompoundRuleDto: IDeployPriorityCompoundRule;

  public showAtomicRuleDialog: boolean = false;

  public showCompoundRuleDialog: boolean = false;

  public gradings: Map<number, IDeployPriorityGrading> = new Map<number, IDeployPriorityGrading>();

  public saving: boolean = false;

  public loading: boolean = false;

  @observable public selectedCompoundRule: IDeployPriorityCompoundRule;

  @observable public selectedAtomicRule: IDeployPriorityAtomicRule;

  @computed public get selectedAtomicRuleUsed() {
    return (
      this.selectedAtomicRule &&
      flatMap(this.compoundRules, (r) => [r.primaryAtomicRuleId, r.secondaryAtomicRuleId]).includes(
        this.selectedAtomicRule.id,
      )
    );
  }

  @observable public isNewCompoundRule: boolean = false;

  @computed public get compoundRuleDialogHeader() {
    return this.isNewCompoundRule ? 'Add Compound Rule' : 'Update Compound Rule';
  }

  @observable public isNewAtomicRule: boolean = false;

  @computed public get atomicRuleDialogHeader() {
    return this.isNewAtomicRule ? 'Add Atomic Rule' : 'Update Atomic Rule';
  }

  constructor(
    private messageService: MessageService,
    private deployPriorityCompoundRulesService: DeployPriorityCompoundRulesService,
    private deployPriorityAtomicRulesService: DeployPriorityAtomicRulesService,
    private jobDeployPriorityGradingsService: DeployPriorityGradingsService,
  ) {}

  @computed public get atomicRuleTextStrings() {
    const entries = this.atomicRules.map((r) => {
      const propString = this.propertyToTitleMapping.get(r.property);
      const operatorString = this.operatorMapping.get(r.operator);
      return [r.id, `${propString} ${operatorString} ${r.comparisonValue}`] as [number, string];
    });
    return new Map<number, string>(entries);
  }

  public primaryAtomicRuleOptions: SelectItem[] = [];

  public secondaryAtomicRuleOptions: SelectItem[] = [];

  public compoundOperatorOptions: SelectItem[] = [
    { label: 'and', value: DeployPriorityCompoundRuleOperator.And },
    { label: 'or', value: DeployPriorityCompoundRuleOperator.Or },
  ];

  @computed public get compoundRuleTextStrings() {
    const entries = this.compoundRules.map((r) => {
      if (r.isElseClause) {
        return [r.id, 'Else'] as [number, string];
      }
      const lhsString = this.atomicRuleTextStrings.get(r.primaryAtomicRuleId) || '';
      const rhsString = this.atomicRuleTextStrings.get(r.secondaryAtomicRuleId) || '';
      const operatorString =
        r.secondaryAtomicRuleId != null
          ? this.compoundOperatorOptions.find((o) => o.value == r.operator).label
          : '';
      return [r.id, `${lhsString} ${operatorString} ${rhsString}`] as [number, string];
    });
    return new Map<number, string>(entries);
  }

  ngOnInit() {
    this.propertyOptions = Array.from(this.propertyToTitleMapping.entries()).map((e) => ({
      label: e[1],
      value: e[0],
    }));
    this.operatorOptions = Array.from(this.operatorMapping.entries()).map((e) => ({
      label: e[1],
      value: e[0],
    }));
    void this.refreshData();
  }

  async refreshData() {
    try {
      this.loading = true;
      await Promise.all([this.getAtomicRules(), this.getCompoundRules(), this.getGradings()]);
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  getGradings() {
    return this.jobDeployPriorityGradingsService.getGradings().then((result) => {
      this.gradings = new Map<number, IDeployPriorityGrading>(
        result.items.map((g) => [g.id, g] as [number, IDeployPriorityGrading]),
      );
      this.gradingOptions = result.items.map((g) => ({
        value: g.id,
      }));
    });
  }

  getAtomicRules() {
    return this.deployPriorityAtomicRulesService.getPage({ rows: 100 }).then((result) => {
      this.atomicRules = result.items;
      this.primaryAtomicRuleOptions = Array.from(this.atomicRuleTextStrings.entries()).map((e) => ({
        label: e[1],
        value: e[0],
      }));
      this.secondaryAtomicRuleOptions = [{ label: 'None', value: null } as SelectItem].concat(
        this.primaryAtomicRuleOptions,
      );
    });
  }

  getCompoundRules() {
    return this.deployPriorityCompoundRulesService.getPage({ rows: 100 }).then((result) => {
      this.compoundRules = result.items.sort((a, b) => a.evaluationOrder - b.evaluationOrder);
    });
  }

  addAtomicRule() {
    this.isNewAtomicRule = true;
    this.updateAtomicRuleDto = {
      id: 0,
      property: DeployPriorityAtomicRuleProperty.SensitivityScore,
      comparisonValue: 0,
      operator: DeployPriorityAtomicRuleOperator.Equals,
    };
    this.showAtomicRuleDialog = true;
  }

  addCompoundRule() {
    this.isNewCompoundRule = true;
    this.updateCompoundRuleDto = {
      id: 0,
      operator: DeployPriorityCompoundRuleOperator.And,
      isElseClause: false,
      deployPriorityGradingId: this.gradingOptions[0].value.id,
      primaryAtomicRuleId: this.primaryAtomicRuleOptions[0].value,
      secondaryAtomicRuleId: null,
      displayOrder: 0,
      evaluationOrder: Math.min(...this.compoundRules.map((r) => r.evaluationOrder)) - 1,
    };
    this.showCompoundRuleDialog = true;
  }

  async saveAtomicRule() {
    this.saving = true;
    try {
      if (this.isNewAtomicRule) {
        await this.deployPriorityAtomicRulesService.create(this.updateAtomicRuleDto);
      } else {
        await this.deployPriorityAtomicRulesService.update(
          this.updateAtomicRuleDto.id,
          this.updateAtomicRuleDto,
        );
      }
      this.showAtomicRuleDialog = false;
      this.updateAtomicRuleDto = null;
      void this.refreshData();
    } catch (e) {
    } finally {
      this.saving = false;
    }
  }

  async saveCompoundRule() {
    this.saving = true;
    try {
      if (this.isNewCompoundRule) {
        await this.deployPriorityCompoundRulesService.create(this.updateCompoundRuleDto);
      } else {
        await this.deployPriorityCompoundRulesService.update(
          this.updateCompoundRuleDto.id,
          this.updateCompoundRuleDto,
        );
      }
      this.showCompoundRuleDialog = false;
      this.updateCompoundRuleDto = null;
      void this.refreshData();
    } catch (e) {
    } finally {
      this.saving = false;
    }
  }

  async moveCompoundRuleUp(index: number) {
    if (index == 0 || index == this.compoundRules.length - 1) {
      return;
    }
    this._move(this.compoundRules, index, index - 1);
    await this.saveCompoundRuleOrder();
  }

  async moveCompoundRuleDown(index: number) {
    if (index > this.compoundRules.length - 3) {
      return;
    }
    this._move(this.compoundRules, index, index + 1);
    await this.saveCompoundRuleOrder();
  }

  async saveCompoundRuleOrder() {
    try {
      this.saving = true;
      await Promise.all(
        this.compoundRules.map((dto, index) => {
          const payload = {
            ...dto,
            evaluationOrder: index,
          };
          return this.deployPriorityCompoundRulesService.update(dto.id, payload, true);
        }),
      );
      this.messageService.add(Message.Success('Compound rule order successfully saved'));
    } catch (e) {
      this.messageService.add(
        Message.Error(
          'An error occurred, order may not be saved correctly. Please reload and try again.',
        ),
      );
    } finally {
      this.saving = false;
    }
  }

  onCompoundRuleSelect() {
    this.isNewCompoundRule = false;
    this.updateCompoundRuleDto = { ...this.selectedCompoundRule };
    this.showCompoundRuleDialog = true;
  }

  onAtomicRuleSelect() {
    this.isNewAtomicRule = false;
    this.updateAtomicRuleDto = { ...this.selectedAtomicRule };
    this.showAtomicRuleDialog = true;
  }

  async deleteAtomicRule() {
    try {
      this.saving = true;
      await this.deployPriorityAtomicRulesService.delete(this.selectedAtomicRule.id);
      this.showAtomicRuleDialog = false;
      await this.refreshData();
    } catch (e) {
    } finally {
      this.saving = false;
    }
  }

  async deleteCompoundRule() {
    try {
      this.saving = true;
      await this.deployPriorityCompoundRulesService.delete(this.selectedCompoundRule.id);
      this.showCompoundRuleDialog = false;
      await this.refreshData();
    } catch (e) {
      this.messageService.add(
        Message.Error(
          'Error deleting compound rule. The rule may have already been assigned to jobs',
        ),
      );
    } finally {
      this.saving = false;
    }
  }

  private _move = function (array: any[], fromIndex: number, toIndex: number) {
    array.splice(toIndex, 0, array.splice(fromIndex, 1)[0]);
  };
}
